import { useContext, useState, useEffect, VFC } from 'react';

import postTokenTransfer from '@/apis/aniftyBackend/aniftyTokens/postTokenTransfer';
import {
  isApprovedForAllERC1155,
  setApprovalForAllERC1155,
} from '@/apis/blockchain/AniftyERC1155';
import { transferV1 } from '@/apis/blockchain/AniftyERC1155V2Ipfs';
import { aniftyTokenV1GraphClient } from '@/apis/theGraph';
import FloatingModal from '@/components/FloatingModal';
import SwapTokenConfirmation from '@/components/SwapWithIPFS/SwapTokenConfirmation';
import SwapWithIPFSButton from '@/components/SwapWithIPFS/SwapWithIPFSButton';
import SwapWithIPFSCompletedContent from '@/components/SwapWithIPFS/SwapWithIPFSCompletedContent';
import TokenTransferApproval from '@/components/SwapWithIPFS/TokenTransferApproval';
import { GraphToken } from '@/components/SwapWithIPFS/types';
import { addresses } from '@/constants/addresses';
import { Web3Context } from '@/contexts/Web3Provider/Web3Provider';
import useAccessToken from '@/hooks/useAccessToken';
import { captureException } from '@/utils/Sentry';

// Transfer flow
type Status = 'waiting' | 'processing' | 'completed';

const SwapWithIPFS: VFC = () => {
  const web3Context = useContext(Web3Context);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [status, setStatus] = useState<Status>('waiting');

  const [userTokens, setUserTokens] =
    useState<undefined | GraphToken[]>(undefined);
  const [isApproved, setIsApproved] = useState<undefined | boolean>(undefined);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [swapTokenCount, setSwapTokenCount] = useState(0);

  const { accessToken, requestSignature } = useAccessToken();
  const isProcessing = status === 'processing';
  const [lastTransactionHash, setLastTransactionHash] =
    useState<string | null>(null);

  const handleRequestCloseModal = () => {
    // Prevent modal closing while processing
    if (!isProcessing) {
      setIsModalOpen(false);
    }

    setStatus('waiting');
  };

  /**
   * Load Status
   */

  const fetchUserTokenData = async () => {
    // query subgraph for userTokens
    const fetchedTokenV1Data =
      await aniftyTokenV1GraphClient.getOwnerInfoByAddress(
        web3Context.currentAccountAddress
      );

    if (fetchedTokenV1Data.owners && fetchedTokenV1Data.owners.length > 0) {
      const tokens = fetchedTokenV1Data.owners[0].tokens;
      // tokens = tokens.filter((token) => token.token.id >= 524);
      setUserTokens(tokens);
    }
  };

  useEffect(() => {
    // Load holding tokens on status change
    fetchUserTokenData();
  }, [status]);

  const checkApproved = async (): Promise<boolean> => {
    if (!web3Context.web3.AniftyERC1155Contract) {
      return;
    }

    try {
      const isApprovedForAll = await isApprovedForAllERC1155(
        web3Context.web3.AniftyERC1155Contract,
        web3Context.currentAccountAddress,
        addresses.AniftyERC1155V2
      );
      setIsApproved(isApprovedForAll);
    } catch (e) {
      console.error(e);
      captureException(e);
    }
  };

  useEffect(() => {
    // Have to approve erc1155v2 to transfer erc1155v1
    // Load approval status on web3 context change
    checkApproved();
  }, [web3Context]);

  /**
   * Transfer tokens
   */

  const handleApproveButtonClick = async () => {
    if (status === 'processing') {
      return;
    }

    setStatus('processing');
    setErrorMessage(null);

    try {
      await setApprovalForAllERC1155(
        web3Context.web3.AniftyERC1155Contract,
        addresses.AniftyERC1155V2,
        true
      );
      await checkApproved();
    } catch (e) {
      console.error(e);
      captureException(e);
      setErrorMessage('Error: Failed to approve tokens');
    } finally {
      setStatus('waiting');
    }
  };

  const handleSwapButtonClick = async (selectedTokens: GraphToken[]) => {
    if (status === 'processing') {
      return;
    }

    setStatus('processing');
    setErrorMessage(null);

    if (!accessToken) {
      const signatureRequestResult = await requestSignature();
      if (!signatureRequestResult) {
        // user cancelled signature request
        setStatus('waiting');
        return;
      }
    }

    try {
      // Filter only selected tokens
      const tokenReq = selectedTokens.map((token) => ({
        tokenId: parseInt(token.token.id),
        amount: parseInt(token.tokenAmount),
      }));
      const transferData = await postTokenTransfer(tokenReq);
      let existingTokenURIs: any = transferData.tokens;
      existingTokenURIs = existingTokenURIs.map((data) => {
        return {
          ...data,
          r: Buffer.from(data.r, 'hex'),
          s: Buffer.from(data.s, 'hex'),
        };
      });

      const tokenTransferTransaction = await transferV1(
        web3Context.web3.AniftyERC1155V2Ipfs,
        transferData.tokenIds,
        transferData.tokenAmounts,
        existingTokenURIs
      );

      setSwapTokenCount(transferData.tokenIds.length);
      setLastTransactionHash(tokenTransferTransaction.transactionHash);

      setStatus('completed');
    } catch (e) {
      console.error(e);
      setErrorMessage('Error: Failed to transfer V1 tokens');
      setStatus('waiting');
    }
  };

  // Make modal wide for token selection
  const modalMaxWidth =
    isApproved && userTokens?.length > 10 ? '550px' : '330px';

  return (
    <>
      <SwapWithIPFSButton onClick={() => setIsModalOpen(true)} />

      <FloatingModal
        isOpen={isModalOpen}
        onRequestClose={handleRequestCloseModal}
        shouldCloseOnEsc={!isProcessing}
        shouldCloseOnOverlayClick={!isProcessing}
        style={{ content: { maxWidth: modalMaxWidth } }}
      >
        {(() => {
          if (status === 'completed') {
            return (
              <SwapWithIPFSCompletedContent
                swappedTokenCount={swapTokenCount}
                lastTransactionHash={lastTransactionHash}
                onCloseButtonClick={handleRequestCloseModal}
              />
            );
          }

          if (typeof userTokens === 'undefined') {
            return <div>Loading token data...</div>;
          }

          if (!isApproved) {
            return (
              <TokenTransferApproval
                userTokens={userTokens}
                isProcessing={isProcessing}
                onConfirm={handleApproveButtonClick}
                errorMessage={errorMessage}
              />
            );
          }

          return (
            <SwapTokenConfirmation
              userTokens={userTokens}
              onSwapButtonClick={handleSwapButtonClick}
              isProcessing={isProcessing}
              errorMessage={errorMessage}
            />
          );
        })()}
      </FloatingModal>
    </>
  );
};

export default SwapWithIPFS;
